import React from 'react';
import {
  ShieldCheck, ShieldWarning, ShieldSlash, WarningOctagon, Info, CheckCircle, XCircle, Bug, Wrench,
} from '@phosphor-icons/react';

/**
 * ProvenanceStrip — the glass-box audit strip that accompanies the hero screen.
 *
 * Sibling of DataConfidenceBanner (src/components/DataConfidenceBanner.tsx), which
 * renders a collapsible top-of-page alert and hides itself entirely when
 * data_confidence === 'high'. This strip is the opposite: always visible, denser,
 * and the one place a user can see *every* quality_flag / defaults_used entry, not
 * just a "something's off" alert. See the report at the end of this task for the
 * overlap call-out between the two.
 *
 * Contract (analysis_results.provenance, backend-defined — do not rename keys):
 *   methodology_version: string
 *   data_confidence: 'high' | 'degraded' | 'low'
 *   quality_flags: { item, status, detail }[]   status ∈ computed | fallback_default | degraded | error
 *   defaults_used: { param, value, reason }[]
 *
 * Honesty rule: a missing value renders "n/d", never a disguised 0/false. This is
 * the one component in the app whose entire job is to admit what was NOT computed
 * from real data — it must never paper over an absence.
 */

export interface QualityFlag {
  item: string;
  status: 'computed' | 'fallback_default' | 'degraded' | 'error' | string;
  detail?: string;
}

export interface DefaultUsed {
  param: string;
  value: unknown;
  reason?: string;
}

export interface Provenance {
  methodology_version?: string;
  data_confidence?: 'high' | 'degraded' | 'low' | string;
  quality_flags?: QualityFlag[];
  defaults_used?: DefaultUsed[];
}

export interface ProvenanceStripProps {
  /** analysis_results.provenance — see contract above. */
  provenance: any;
  isDark: boolean;
  language: 'es' | 'en';
}

const confidenceMeta = (confidence: string | undefined, isEs: boolean) => {
  switch (confidence) {
    case 'high':
      return { icon: <ShieldCheck size={16} weight="duotone" />, badge: 'badge-success', label: isEs ? 'Alta' : 'High' };
    case 'degraded':
      return { icon: <ShieldWarning size={16} weight="duotone" />, badge: 'badge-warning', label: isEs ? 'Degradada' : 'Degraded' };
    case 'low':
      return { icon: <ShieldSlash size={16} weight="duotone" />, badge: 'badge-danger', label: isEs ? 'Baja' : 'Low' };
    default:
      return { icon: <Info size={16} weight="duotone" />, badge: 'badge-neutral', label: 'n/d' };
  }
};

const flagMeta = (status: string, isEs: boolean) => {
  switch (status) {
    case 'fallback_default':
      return { icon: <Wrench size={11} weight="bold" />, badge: 'badge-warning', label: isEs ? 'Por defecto' : 'Fallback' };
    case 'degraded':
      return { icon: <WarningOctagon size={11} weight="bold" />, badge: 'badge-warning', label: isEs ? 'Degradado' : 'Degraded' };
    case 'error':
      return { icon: <XCircle size={11} weight="bold" />, badge: 'badge-danger', label: isEs ? 'Error' : 'Error' };
    default:
      return { icon: <Bug size={11} weight="bold" />, badge: 'badge-neutral', label: status };
  }
};

const ProvenanceStrip: React.FC<ProvenanceStripProps> = ({ provenance, isDark, language }) => {
  void isDark; // surfaces are theme-aware via CSS custom properties (--aipron-*), no branching needed
  const isEs = language === 'es';
  const prov: Provenance | undefined = provenance && typeof provenance === 'object' ? provenance : undefined;

  // Honest empty state: older analyses (pre glass-box audit) simply have no
  // provenance object. Say so plainly instead of rendering a fake "all good".
  if (!prov) {
    return (
      <div className="pro-card p-4">
        <div className="flex items-center gap-2">
          <Info size={16} className="text-[var(--text-tertiary)]" weight="duotone" />
          <span className="eyebrow">{isEs ? 'Glass-box · Auditoría de datos' : 'Glass-box · Data audit'}</span>
        </div>
        <p className="text-xs text-[var(--text-tertiary)] mt-2">
          {isEs
            ? 'n/d — este análisis no registra información de procedencia (proyecto anterior a la auditoría glass-box).'
            : 'n/d — this analysis has no provenance recorded (project predates the glass-box audit).'}
        </p>
      </div>
    );
  }

  const meta = confidenceMeta(prov.data_confidence, isEs);
  const version = prov.methodology_version ?? 'n/d';
  const flags = Array.isArray(prov.quality_flags) ? prov.quality_flags : undefined;
  const defaults = Array.isArray(prov.defaults_used) ? prov.defaults_used : undefined;
  const nonComputed = (flags ?? []).filter((f) => f.status !== 'computed');
  const computedCount = (flags ?? []).filter((f) => f.status === 'computed').length;

  return (
    <div className="pro-card p-4">
      {/* Header: overall confidence + methodology version + computed ratio */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {meta.icon}
          <span className="eyebrow">{isEs ? 'Glass-box · Auditoría de datos' : 'Glass-box · Data audit'}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-[var(--text-tertiary)]">
            {isEs ? 'Metodología' : 'Methodology'} v{version}
          </span>
          <span className={`badge ${meta.badge}`}>{meta.label}</span>
          {flags !== undefined && (
            <span className="badge badge-neutral">
              {computedCount}/{flags.length} {isEs ? 'calculado' : 'computed'}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Quality flags — only the non-'computed' entries are worth auditing */}
        <div>
          <div className="kpi-label mb-1.5">
            {isEs ? `Incidencias (${nonComputed.length})` : `Flags (${nonComputed.length})`}
          </div>
          {flags === undefined ? (
            <p className="text-xs text-[var(--text-tertiary)]">n/d</p>
          ) : nonComputed.length === 0 ? (
            <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5">
              <CheckCircle size={12} weight="bold" className="text-[var(--aipron-success)]" />
              {isEs ? 'Todos los indicadores se calcularon con datos reales.' : 'Every indicator was computed from real data.'}
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
              {nonComputed.map((f, i) => {
                const fm = flagMeta(f.status, isEs);
                return (
                  <li key={`${f.item}-${i}`} className="text-xs flex items-start gap-1.5">
                    <span className={`badge ${fm.badge} shrink-0 mt-0.5`}>{fm.icon}{fm.label}</span>
                    <span className="text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">{f.item}</span>
                      {f.detail ? <span> — {f.detail}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Defaults used — the honest "this was fabricated, not measured" ledger */}
        <div>
          <div className="kpi-label mb-1.5">
            {isEs ? `Valores por defecto (${defaults?.length ?? 0})` : `Defaults used (${defaults?.length ?? 0})`}
          </div>
          {defaults === undefined ? (
            <p className="text-xs text-[var(--text-tertiary)]">n/d</p>
          ) : defaults.length === 0 ? (
            <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5">
              <CheckCircle size={12} weight="bold" className="text-[var(--aipron-success)]" />
              {isEs ? 'No se recurrió a ningún valor por defecto.' : 'No default values were used.'}
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
              {defaults.map((d, i) => (
                <li key={`${d.param}-${i}`} className="text-xs text-[var(--text-secondary)]">
                  <span className="font-mono font-semibold text-[var(--text-primary)]">{d.param}</span>
                  <span className="font-mono"> = {String(d.value)}</span>
                  {d.reason ? <span className="text-[var(--text-tertiary)]"> — {d.reason}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvenanceStrip;
